import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { Spinner, ErrBanner } from '../../hooks/useApi';
import ConfirmModal from '../../components/ConfirmModal';

/**
 * Company Members — owners of OTHER companies who have been granted access to
 * THIS company (rows in company_members), plus any still-pending invitations.
 *
 * These people are NOT sub-users (user_accounts), so they never appear in the
 * Users tab — this is the only place to see and manage them. Backed by
 * companies.php?action=members (list) and ?action=revoke (remove access).
 *
 * Owner-only: the backend rejects sub-users; we also render a clear notice.
 */
export default function CompanyMembers() {
  const { t } = useTranslation();
  const [data, setData]       = useState(null);   // { members, invitations }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [msg, setMsg]         = useState('');
  const [confirm, setConfirm] = useState(null);   // member row pending revoke
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.companies.members()
      .then(res => setData(res || { members: [], invitations: [] }))
      .catch(e => setError(e?.message || t('userManagement.members.couldNotLoad')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    const m = confirm;
    setConfirm(null);
    try {
      await api.companies.revoke({ member_client_id: m.member_client_id });
      notify('✅ ' + t('userManagement.members.accessRevoked'));
      load();
    } catch (e) { notify('❌ ' + (e?.message || t('userManagement.members.couldNotRevoke'))); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrBanner msg={error} />;

  const members     = (data?.members || []);
  const activeCount = members.filter(m => m.status === 'active').length;
  const invitations = (data?.invitations || []);

  return (
    <div className="user-section">
      <div className="section-header">
        <span>{t('userManagement.members.countHeader', { n: activeCount })}</span>
      </div>

      {msg && <div className="um-toast">{msg}</div>}

      <p style={{ margin: '8px 0 16px', color: 'var(--text-muted)', fontSize: 13 }}>
        {t('userManagement.members.intro1')}<b>{t('userManagement.members.usersTab')}</b>{t('userManagement.members.intro2')}
      </p>

      <div className="section-card" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('common.name')}</th>
              <th style={{ textAlign: 'left' }}>{t('userManagement.members.thEmail')}</th>
              <th style={{ textAlign: 'left' }}>{t('userManagement.members.thRole')}</th>
              <th>{t('common.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                {t('userManagement.members.noMembers')}
              </td></tr>
            ) : members.map(m => (
              <tr key={m.id} style={{ opacity: m.status === 'active' ? 1 : 0.55 }}>
                <td style={{ textAlign: 'left', fontWeight: 600 }}>{m.name || '—'}</td>
                <td style={{ textAlign: 'left' }}>{m.email || '—'}</td>
                <td style={{ textAlign: 'left' }}>
                  <span className="um-role-pill">{m.role_name || t('userManagement.members.member')}</span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {m.status === 'active'
                    ? <span style={{ color: 'var(--success, #1e6e3b)', fontWeight: 600 }}>{t('common.active')}</span>
                    : <span style={{ color: 'var(--text-muted)' }}>{t('userManagement.members.revoked')}</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {m.status === 'active' && (
                    <button className="btn btn-sm btn-danger" onClick={() => setConfirm(m)}>
                      {t('userManagement.members.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invitations.length > 0 && (
        <div className="section-card" style={{ marginTop: 16, overflowX: 'auto' }}>
          <div className="section-header">{t('userManagement.members.pendingInvitations', { n: invitations.length })}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t('userManagement.members.thEmail')}</th>
                <th style={{ textAlign: 'left' }}>{t('userManagement.members.thSent')}</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.id}>
                  <td style={{ textAlign: 'left' }}>{inv.invited_email}</td>
                  <td style={{ textAlign: 'left' }}>{inv.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('userManagement.members.revokeTitle')}
        message={confirm ? t('userManagement.members.revokeMsg', { who: confirm.name || confirm.email }) : ''}
        confirmLabel={t('userManagement.members.revoke')}
        variant="danger"
        onConfirm={revoke}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
